<script setup>
import { ref, computed } from 'vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import BaseBubble from './Base.vue';
import PostContent from './ActivityContent/PostContent.vue';
import ItemsContent from './ActivityContent/ItemsContent.vue';
import { useMessageContext } from '../provider.js';
import Icon from 'next/icon/Icon.vue';

const { content, contentAttributes, createdAt } = useMessageContext();

const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, h:mm a')
);

const hasAdditionalContent = computed(() => {
  return Object.keys(contentAttributes.value).length !== 0;
});

const isPostActivityMessage = computed(() => {
  return contentAttributes.value.activityType === 'post';
});

const items = computed(() => {
  return contentAttributes.value.items || [];
});

const toggleExpand = ref(false);
</script>

<template>
  <BaseBubble
    v-tooltip.top="readableTime"
    class="!rounded-xl flex flex-col items-center min-w-0 gap-2 mb-4"
    data-bubble-name="activity"
  >
    <span class="text-sm text-center px-4 py-2">
      <span v-dompurify-html="content" :title="content" />
      <a
        v-if="contentAttributes.link"
        class="underline text-n-blue-text ms-1"
        rel="noopener noreferrer"
        target="_blank"
        :href="contentAttributes.link"
      >
        {{ $t('MESSAGES.ACTIVITY_MESSAGE.VIEW_DETAILS') }}
      </a>
    </span>
    <div
      v-if="hasAdditionalContent"
      class="flex flex-col items-center gap-2 py-1 w-full"
    >
      <div
        class="w-full space-y-2 px-4 relative"
        :class="toggleExpand ? '' : 'max-h-[2rem] overflow-hidden'"
      >
        <div
          v-if="!toggleExpand"
          class="absolute w-full h-full left-0 top-0 bg-gradient-to-b from-transparent to-slate-500 z-10 opacity-50"
        />
        <PostContent
          v-if="isPostActivityMessage"
          :content-attributes="contentAttributes"
          :toggle-expand="toggleExpand"
        />
        <ItemsContent
          v-if="items.length"
          :content-attributes="contentAttributes"
          :items="items"
        />
      </div>
      <button @click="toggleExpand = !toggleExpand">
        <Icon
          v-if="toggleExpand"
          icon="i-lucide-chevron-up"
          class="w-4 h-4 inline-block"
        />
        <Icon
          v-if="!toggleExpand"
          icon="i-lucide-chevron-down"
          class="w-4 h-4 inline-block"
        />
      </button>
    </div>
  </BaseBubble>
</template>
